'use client';

import { useEffect, useState } from 'react';
import { Check, Lightbulb, Loader2, SendHorizonal, Zap } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { SmartIcon } from '@/shared/blocks/common';
import { PaymentModal } from '@/shared/blocks/payment/payment-modal';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useAppContext } from '@/shared/contexts/app';
import { getCookie } from '@/shared/lib/cookie';
import { cn } from '@/shared/lib/utils';
import { Subscription } from '@/shared/models/subscription';
import { PricingCurrency, PricingItem, Pricing as PricingType } from '@/shared/types/blocks/pricing';

// Helper function to get all available currencies from a pricing item
function getCurrenciesFromItem(item: PricingItem | null): PricingCurrency[] {
  if (!item) return [];

  // Always include the default currency first
  const defaultCurrency: PricingCurrency = {
    currency: item.currency,
    amount: item.amount,
    price: item.price || '',
    original_price: item.original_price || ''
  };

  // Add additional currencies if available
  if (item.currencies && item.currencies.length > 0) {
    return [defaultCurrency, ...item.currencies];
  }
  return [defaultCurrency];
}

// Helper function to select initial currency based on locale
function getInitialCurrency(currencies: PricingCurrency[], locale: string, defaultCurrency: string): string {
  if (currencies.length === 0) return defaultCurrency;

  // If locale is 'zh', prefer CNY
  if (locale === 'zh') {
    const cnyCurrency = currencies.find(c => c.currency.toLowerCase() === 'cny');
    if (cnyCurrency) {
      return cnyCurrency.currency;
    }
  }

  // Otherwise return default currency
  return defaultCurrency;
}
export function Pricing({
  pricing,
  className,
  currentSubscription
}: {
  pricing: PricingType;
  className?: string;
  currentSubscription?: Subscription;
}) {
  const locale = useLocale();
  const t = useTranslations('pricing.page');
  const {
    user,
    isShowPaymentModal,
    setIsShowSignModal,
    setIsShowPaymentModal,
    configs
  } = useAppContext();
  const [group, setGroup] = useState(() => {
    // find current pricing item
    const currentItem = pricing.items?.find(i => i.product_id === currentSubscription?.productId);

    // First look for a group with is_featured set to true
    const featuredGroup = pricing.groups?.find(g => g.is_featured);
    // If no featured group exists, fall back to the first group
    return currentItem?.group || featuredGroup?.name || pricing.groups?.[0]?.name;
  });

  // current pricing item
  const [pricingItem, setPricingItem] = useState<PricingItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);

  // Currency state management for each item
  // Store selected currency and displayed item for each product_id
  const [itemCurrencies, setItemCurrencies] = useState<Record<string, {
    selectedCurrency: string;
    displayedItem: PricingItem;
  }>>({});

  // Initialize currency states for all items
  useEffect(() => {
    if (pricing.items && pricing.items.length > 0) {
      const initialCurrencyStates: Record<string, {
        selectedCurrency: string;
        displayedItem: PricingItem;
      }> = {};
      pricing.items.forEach(item => {
        const currencies = getCurrenciesFromItem(item);
        const selectedCurrency = getInitialCurrency(currencies, locale, item.currency);

        // Create displayed item with selected currency
        const currencyData = currencies.find(c => c.currency.toLowerCase() === selectedCurrency.toLowerCase());
        const displayedItem = currencyData ? {
          ...item,
          currency: currencyData.currency,
          amount: currencyData.amount,
          price: currencyData.price,
          original_price: currencyData.original_price,
          // Override with currency-specific payment settings if available
          payment_product_id: currencyData.payment_product_id || item.payment_product_id,
          payment_providers: currencyData.payment_providers || item.payment_providers
        } : item;
        initialCurrencyStates[item.product_id] = {
          selectedCurrency,
          displayedItem
        };
      });
      setItemCurrencies(initialCurrencyStates);
    }
  }, [pricing.items, locale]);

  // Handler for currency change
  const handleCurrencyChange = (productId_0: string, currency: string) => {
    const item_0 = pricing.items?.find(i_0 => i_0.product_id === productId_0);
    if (!item_0) return;
    const currencies_0 = getCurrenciesFromItem(item_0);
    const currencyData_0 = currencies_0.find(c_0 => c_0.currency.toLowerCase() === currency.toLowerCase());
    if (currencyData_0) {
      const displayedItem_0 = {
        ...item_0,
        currency: currencyData_0.currency,
        amount: currencyData_0.amount,
        price: currencyData_0.price,
        original_price: currencyData_0.original_price,
        // Override with currency-specific payment settings if available
        payment_product_id: currencyData_0.payment_product_id || item_0.payment_product_id,
        payment_providers: currencyData_0.payment_providers || item_0.payment_providers
      };
      setItemCurrencies(prev => ({
        ...prev,
        [productId_0]: {
          selectedCurrency: currency,
          displayedItem: displayedItem_0
        }
      }));
    }
  };
  const handlePayment = async (item_1: PricingItem) => {
    if (!user) {
      setIsShowSignModal(true);
      return;
    }

    // Use displayed item with selected currency
    const displayedItem_1 = itemCurrencies[item_1.product_id]?.displayedItem || item_1;
    if (configs.select_payment_enabled === 'true') {
      setPricingItem(displayedItem_1);
      setIsShowPaymentModal(true);
    } else {
      handleCheckout(displayedItem_1, configs.default_payment_provider);
    }
  };
  const getAffiliateMetadata = ({
    paymentProvider
  }: {
    paymentProvider: string;
  }) => {
    const affiliateMetadata: Record<string, string> = {};

    // get Affonso referral
    if (configs.affonso_enabled === 'true' && ['stripe', 'creem'].includes(paymentProvider)) {
      const affonsoReferral = getCookie('affonso_referral') || '';
      affiliateMetadata.affonso_referral = affonsoReferral;
    }

    // get PromoteKit referral
    if (configs.promotekit_enabled === 'true' && ['stripe'].includes(paymentProvider)) {
      const promotekitReferral = typeof window !== 'undefined' && (window as any).promotekit_referral ? (window as any).promotekit_referral : getCookie('promotekit_referral') || '';
      affiliateMetadata.promotekit_referral = promotekitReferral;
    }
    return affiliateMetadata;
  };
  const handleCheckout = async (item_2: PricingItem, paymentProvider_0?: string) => {
    try {
      if (!user) {
        setIsShowSignModal(true);
        return;
      }
      const affiliateMetadata_0 = getAffiliateMetadata({
        paymentProvider: paymentProvider_0 || ''
      });
      const params = {
        product_id: item_2.product_id,
        currency: item_2.currency,
        locale: locale || 'en',
        payment_provider: paymentProvider_0 || '',
        metadata: affiliateMetadata_0
      };
      setIsLoading(true);
      setProductId(item_2.product_id);
      const response = await fetch('/api/payment/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      if (response.status === 401) {
        setIsLoading(false);
        setProductId(null);
        setPricingItem(null);
        setIsShowSignModal(true);
        return;
      }
      if (!response.ok) {
        throw new Error(`request failed with status ${response.status}`);
      }
      const {
        code,
        message,
        data
      } = await response.json();
      if (code !== 0) {
        throw new Error(message);
      }
      const {
        checkoutUrl
      } = data;
      if (!checkoutUrl) {
        throw new Error('checkout url not found');
      }
      window.location.href = checkoutUrl;
    } catch (e: any) {
      console.log('checkout failed: ', e);
      toast.error('checkout failed: ' + e.message);
      setIsLoading(false);
      setProductId(null);
    }
  };
  useEffect(() => {
    if (pricing.items) {
      const featuredItem = pricing.items.find(i_1 => i_1.is_featured);
      setProductId(featuredItem?.product_id || pricing.items[0]?.product_id);
      setIsLoading(false);
    }
  }, [pricing.items]);
  return <section id={pricing.id} className={cn('py-24 md:py-36', pricing.className, className)}>
      <div className="mx-auto mb-12 px-4 text-center md:px-8">
        {pricing.sr_only_title && <h1 className="sr-only">{pricing.sr_only_title}</h1>}
        <h2 className="mb-6 text-3xl font-bold text-pretty lg:text-4xl">
          {pricing.title}
        </h2>
        <p className="text-muted-foreground mx-auto mb-4 max-w-xl lg:max-w-none lg:text-lg">
          {pricing.description}
        </p>
      </div>

      <div className="container">
        {pricing.groups && pricing.groups.length > 0 && <div className="mx-auto mt-8 mb-16 flex w-full justify-center md:max-w-lg">
            <Tabs value={group} onValueChange={setGroup} className="">
              <TabsList>
                {pricing.groups.map((item_3, i_2) => {
              return <TabsTrigger key={i_2} value={item_3.name || ''}>
                      {item_3.title}
                      {item_3.label && <Badge className="ml-2">{item_3.label}</Badge>}
                    </TabsTrigger>;
            })}
              </TabsList>
            </Tabs>
          </div>}

        <div className={`mx-auto mt-0 grid w-full gap-6 md:grid-cols-${pricing.items?.filter(item_4 => !item_4.group || item_4.group === group)?.length}`}>
          {pricing.items?.map((item_5: PricingItem, idx) => {
          if (item_5.group && item_5.group !== group) {
            return null;
          }
          let isCurrentPlan = false;
          if (currentSubscription && currentSubscription.productId === item_5.product_id) {
            isCurrentPlan = true;
          }

          // Get currency state for this item
          const currencyState = itemCurrencies[item_5.product_id];
          const displayedItem_2 = currencyState?.displayedItem || item_5;
          const selectedCurrency_0 = currencyState?.selectedCurrency || item_5.currency;
          const currencies_1 = getCurrenciesFromItem(item_5);
          return <Card key={idx} className="relative">
                {item_5.label && <span className="absolute inset-x-0 -top-3 mx-auto flex h-6 w-fit items-center rounded-full bg-linear-to-br/increasing from-purple-400 to-amber-300 px-3 py-1 text-xs font-medium text-amber-950 ring-1 ring-white/20 ring-offset-1 ring-offset-gray-950/5 ring-inset">
                    {item_5.label}
                  </span>}

                <CardHeader>
                  <CardTitle className="font-medium">
                    <h3 className="text-sm font-medium">{item_5.title}</h3>
                  </CardTitle>

                  <div className="my-3 flex items-baseline gap-2">
                    {displayedItem_2.original_price && <span className="text-muted-foreground text-sm line-through">
                        {displayedItem_2.original_price}
                      </span>}

                    <div className="my-3 block text-2xl font-semibold">
                      <span className="text-primary">
                        {displayedItem_2.price}
                      </span>{' '}
                      {displayedItem_2.unit ? <span className="text-muted-foreground text-sm font-normal">
                          {displayedItem_2.unit}
                        </span> : ''}
                    </div>

                    {currencies_1.length > 1 && <Select value={selectedCurrency_0} onValueChange={currency_0 => handleCurrencyChange(item_5.product_id, currency_0)}>
                        <SelectTrigger size="sm" className="border-muted-foreground/30 bg-background/50 h-6 min-w-[60px] px-2 text-xs">
                          <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {currencies_1.map(currency_1 => <SelectItem key={currency_1.currency} value={currency_1.currency} className="text-xs">
                              {currency_1.currency.toUpperCase()}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>}
                  </div>

                  <CardDescription className="text-sm">
                    {item_5.description}
                  </CardDescription>
                  {item_5.tip && <span className="text-muted-foreground text-sm">
                      {item_5.tip}
                    </span>}

                  {isCurrentPlan ? <Button variant="outline" className="mt-4 h-9 w-full px-4 py-2" disabled>
                      <span className="hidden text-sm md:block">
                        {t('current_plan')}
                      </span>
                    </Button> : <Button onClick={() => handlePayment(item_5)} disabled={isLoading} className={cn('focus-visible:ring-ring inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50', 'mt-4 h-9 w-full px-4 py-2', 'bg-primary text-primary-foreground hover:bg-primary/90 border-[0.5px] border-white/25 shadow-md shadow-black/20')}>
                      {isLoading && item_5.product_id === productId ? <>
                          <Loader2 className="size-4 animate-spin" />
                          <span className="block">{t('processing')}</span>
                        </> : <>
                          {item_5.button?.icon && <SmartIcon name={item_5.button?.icon as string} className="size-4" />}
                          <span className="block">{item_5.button?.title}</span>
                        </>}
                    </Button>}
                </CardHeader>

                <CardContent className="space-y-4">
                  <hr className="border-dashed" />

                  {item_5.features_title && <p className="text-sm font-medium">{item_5.features_title}</p>}
                  <ul className="list-outside space-y-3 text-sm">
                    {item_5.features?.map((item_6, index) => <li key={index} className="flex items-center gap-2">
                        <Check className="size-3" />
                        {item_6}
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>

      <PaymentModal isLoading={isLoading} pricingItem={pricingItem} onCheckout={(item_7, paymentProvider_1) => handleCheckout(item_7, paymentProvider_1)} />
    </section>;
}